import { useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { IProduct } from "integrations/firebase/collections";
import { useApi } from "hooks/useApi";
import { useAnalytics } from "contexts/AnalyticsContext";
import { toast } from "utils/toast";
import { useProjectDetails } from "../../contexts/ProjectDetailsContext";
import { BulkGenerateOption } from "./BulkGenerateOption";

interface GenerateAPlusContentOptionProps {
  products: IProduct[];
  onDone: () => void;
}

export const GenerateAPlusContentOption = ({
  products,
  onDone,
}: GenerateAPlusContentOptionProps) => {
  const { t } = useTranslation(["projectDetails"]);
  const api = useApi();
  const { gaEvent } = useAnalytics();
  const { project, setAmplifyProjectId } = useProjectDetails();
  const [isAdding, setIsAdding] = useState(false);

  const addBooksToAmplify = async (projectId: string) => {
    const added = await api.projects.addProductsToAmplify(
      projectId,
      products.map((product) => product.id),
    );
    if (!added) throw new Error("Amplify response has no data");

    setAmplifyProjectId(projectId, added.amplifyProjectId);
    gaEvent({
      type: "project_amplify_books_added",
      payload: { books_count: products.length, added_count: added.addedCount },
    });
  };

  const handleClick = async (event: MouseEvent) => {
    // Keeps the menu open, so the item can show its spinner.
    event.preventDefault();
    if (!project) return;

    setIsAdding(true);
    try {
      await addBooksToAmplify(project.id);
    } catch {
      toast.error(t("projectDetails:buttons.amplify.error"));
      gaEvent({
        type: "project_amplify_books_add_failed",
        payload: { books_count: products.length },
      });
    } finally {
      setIsAdding(false);
      onDone();
    }
  };

  return (
    <BulkGenerateOption
      disabled={isAdding}
      loading={isAdding}
      onClick={handleClick}
      label={t("projectDetails:buttons.amplify.items.generateAPlusContent")}
    />
  );
};
